import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import Header from '../components/Header';
import { getCheckoutStatus } from '../lib/api';

const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

const Confirmation = ({ cartItems, onRemoveFromCart, onUpdateQuantity, onClearCart }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [pollStatus, setPollStatus] = useState('checking'); // checking | paid | failed | timeout | expired
  const [order, setOrder] = useState(null);
  const cartClearedRef = useRef(false);
  // Keep latest onClearCart without retriggering the polling effect
  const onClearCartRef = useRef(onClearCart);
  useEffect(() => {
    onClearCartRef.current = onClearCart;
  }, [onClearCart]);

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }
    let attempts = 0;
    let timer;
    let cancelled = false;

    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setOrder(data.order);
          setPollStatus('paid');
          // Persist for design details handoff & clear cart only once
          if (data.order) {
            sessionStorage.setItem('lastOrder', JSON.stringify(data.order));
          }
          if (!cartClearedRef.current && onClearCartRef.current) {
            onClearCartRef.current();
            cartClearedRef.current = true;
          }
          return;
        }
        if (data.status === 'expired') {
          setPollStatus('expired');
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setPollStatus('timeout');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        console.error('Checkout status poll failed:', err);
        if (cancelled) return;
        attempts += 1;
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setPollStatus('failed');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, navigate]);

  if (pollStatus === 'checking') {
    return (
      <div className="min-h-screen bg-warm-ivory">
        <Header cartItems={cartItems} onRemoveFromCart={onRemoveFromCart} onUpdateQuantity={onUpdateQuantity} />
        <section className="pt-40 md:pt-44 pb-20 px-6">
          <div className="container mx-auto max-w-3xl">
            <Card className="border-stone-taupe/20 text-center">
              <CardContent className="pt-12 pb-12">
                <Loader2 className="w-12 h-12 text-charcoal-ink mx-auto mb-4 animate-spin" />
                <h1 className="text-2xl font-bold text-charcoal-ink mb-2">Verifying your payment…</h1>
                <p className="text-stone-taupe">Please wait a moment.</p>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  if (pollStatus !== 'paid') {
    const messages = {
      expired: 'Your checkout session expired. Please try again.',
      failed: 'We could not confirm your payment. Please try again or contact support.',
      timeout: "Payment is still processing. We'll email you once it completes.",
    };
    return (
      <div className="min-h-screen bg-warm-ivory">
        <Header cartItems={cartItems} onRemoveFromCart={onRemoveFromCart} onUpdateQuantity={onUpdateQuantity} />
        <section className="pt-40 md:pt-44 pb-20 px-6">
          <div className="container mx-auto max-w-3xl">
            <Card className="border-stone-taupe/20 text-center">
              <CardContent className="pt-12 pb-12">
                <XCircle className="w-16 h-16 text-charcoal-ink mx-auto mb-4" />
                <h1 className="text-3xl font-bold text-charcoal-ink mb-4">Payment {pollStatus === 'expired' ? 'Expired' : 'Pending'}</h1>
                <p className="text-stone-taupe mb-8">{messages[pollStatus]}</p>
                <div className="flex gap-3 justify-center">
                  <Link to="/checkout">
                    <Button className="bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory">Try Again</Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="border-charcoal-ink text-charcoal-ink">Back to Home</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-ivory">
      <Header cartItems={cartItems} onRemoveFromCart={onRemoveFromCart} onUpdateQuantity={onUpdateQuantity} />
      <section className="pt-40 md:pt-44 pb-20 px-6">
        <div className="container mx-auto max-w-3xl">
          <Card className="border-stone-taupe/20 text-center">
            <CardContent className="pt-12 pb-12">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-charcoal-ink flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-warm-ivory" />
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-charcoal-ink mb-4" data-testid="confirmation-heading">
                You're officially booked!
              </h1>

              <p className="text-xl text-stone-taupe mb-2">
                Thank you for your order. Your purchase reserves your design spot with The Favor.
              </p>

              <div className="bg-soft-linen p-6 rounded-lg my-8">
                <p className="text-charcoal-ink font-medium mb-1">Order Number</p>
                <p className="text-2xl font-bold text-charcoal-ink" data-testid="confirmation-order-number">{order?.orderNumber}</p>
              </div>

              <div className="bg-warm-ivory border-2 border-charcoal-ink/10 p-8 rounded-lg mb-8">
                <h2 className="text-2xl font-bold text-charcoal-ink mb-4">Next Step:</h2>
                <p className="text-lg text-stone-taupe mb-6">
                  Please complete the Design Details Form so we can begin creating your custom order.
                </p>

                <p className="text-sm text-stone-taupe mb-6 italic">
                  Please note: Final pricing for printed items is confirmed once quantities and specifications are reviewed.
                </p>

                <Link to="/design-details">
                  <Button
                    size="lg"
                    data-testid="confirmation-design-details-button"
                    className="bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory text-lg px-8 py-6"
                  >
                    Complete Design Details Form
                  </Button>
                </Link>
              </div>

              {order?.customerInfo?.email && (
                <p className="text-sm text-stone-taupe">
                  A confirmation will be sent to {order.customerInfo.email}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Confirmation;
